import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Eye, Calendar, Bell } from 'lucide-react';
import { getJobViewAnalytics, getApplicationMetricsForTeam } from '../../../services/applicationsService';
import { useMobileResponsive } from '../../../hooks/useMobileResponsive';

const AnalyticsReports = () => {
  const { isMobile } = useMobileResponsive();
  const [isLoading, setIsLoading] = useState(false);
  const [jobViewAnalytics, setJobViewAnalytics] = useState([]);
  const [applicationMetrics, setApplicationMetrics] = useState(null);

  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setIsLoading(true);
        const companyId = 'your-company-id'; // Get from auth context

        // Load job view analytics
        const viewAnalytics = await getJobViewAnalytics(companyId);
        setJobViewAnalytics(viewAnalytics);

        // Load application metrics
        const appMetrics = await getApplicationMetricsForTeam(companyId);
        setApplicationMetrics(appMetrics);

      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalyticsData();
  }, []);

  const upcomingFeatures = [
    {
      icon: BarChart3,
      title: "Job Performance Analytics",
      description: "Track job views, applications, and conversion rates"
    },
    {
      icon: TrendingUp,
      title: "Hiring Trends & Insights",
      description: "Industry benchmarks and market analysis"
    },
    {
      icon: Users,
      title: "Candidate Pipeline Reports",
      description: "Detailed hiring funnel and stage conversion metrics"
    },
    {
      icon: Eye,
      title: "Company Profile Visibility",
      description: "Monitor your company\'s reach and engagement"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-full mb-6">
            <BarChart3 className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Analytics & Reports
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Comprehensive insights and data-driven reports to optimize your hiring process are coming soon.
          </p>
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            <Calendar className="w-4 h-4 mr-2" />
            Expected Q2 2025
          </div>
        </div>
      </div>
      {/* Feature Preview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {upcomingFeatures?.map((feature, index) => (
          <div 
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-gray-600" />
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature?.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature?.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Notification Signup */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8">
        <div className="text-center">
          <Bell className="w-8 h-8 text-blue-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Get Notified When It's Ready
          </h3>
          <p className="text-gray-600 mb-6">
            Be the first to know when our analytics and reporting features go live.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
              Notify Me
            </button>
          </div>
        </div>
      </div>
      {/* Current Workaround */}
      <div className="mt-12 text-center">
        <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Need Analytics Now?
          </h4>
          <p className="text-gray-600 mb-4">
            In the meantime, you can track basic job metrics in the Jobs section and monitor team activity in Team Management.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              View Jobs Dashboard
            </button>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              Team Activity
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReports;